import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  0%   { opacity: 0; transform: scale(0.5) rotate(-10deg); }
  60%  { opacity: 1; transform: scale(1.08) rotate(2deg); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50%      { transform: scale(1.12); opacity: 1; }
`;

const crossGrow = keyframes`
  0%   { transform: scale(0) rotate(45deg); opacity: 0; }
  50%  { transform: scale(1.15) rotate(0deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40%           { transform: scale(1); }
`;

const floatParticle = keyframes`
  0%   { transform: translateY(0) scale(1); opacity: 0.3; }
  50%  { transform: translateY(-30px) scale(1.3); opacity: 0.6; }
  100% { transform: translateY(-60px) scale(0.8); opacity: 0; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; visibility: hidden; }
`;

const SPLASH_DURATION_MS = 3200;
const FADE_OUT_MS = 600;

export default function SplashScreen({ onFinished }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const exitTimer = setTimeout(() => setExiting(true), SPLASH_DURATION_MS);
    const doneTimer = setTimeout(() => onFinished(), SPLASH_DURATION_MS + FADE_OUT_MS);
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [onFinished]);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(160deg, #6B4F28 0%, #B8894A 40%, #D4A574 75%, #B8894A 100%)',
        overflow: 'hidden',
        animation: exiting ? `${fadeOut} ${FADE_OUT_MS}ms ease-in forwards` : 'none',
      }}
    >
      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: { xs: 6, md: 8 },
            height: { xs: 6, md: 8 },
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.15)',
            left: `${15 + i * 14}%`,
            bottom: '10%',
            animation: `${floatParticle} ${3 + i * 0.5}s ease-in-out infinite`,
            animationDelay: `${i * 0.4}s`,
          }}
        />
      ))}

      {/* Radial glow behind logo */}
      <Box
        sx={{
          position: 'absolute',
          width: { xs: 280, md: 400 },
          height: { xs: 280, md: 400 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
          animation: `${pulse} 3s ease-in-out infinite`,
        }}
      />

      {/* Logo container */}
      <Box
        sx={{
          position: 'relative',
          mb: 3,
          animation: `${scaleIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
        }}
      >
        <Box
          sx={{
            width: { xs: 110, md: 140 },
            height: { xs: 110, md: 140 },
            borderRadius: '28%',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 0 0 4px rgba(255,255,255,0.2)',
          }}
        >
          <Box
            component="img"
            src="/logo512.png"
            alt="Crystal Bardawam"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      </Box>

      {/* App name */}
      <Typography
        variant="h4"
        sx={{
          color: '#fff',
          fontWeight: 900,
          letterSpacing: 1.5,
          fontSize: { xs: '1.65rem', md: '2.2rem' },
          animation: `${fadeIn} 0.7s ease-out 0.4s both`,
          textShadow: '0 2px 16px rgba(0,0,0,0.18)',
        }}
      >
        Crystal Bardawam
      </Typography>

      {/* Medical cross accent */}
      <Box
        sx={{
          mt: 2,
          animation: `${crossGrow} 0.6s ease-out 0.8s both`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 28,
            height: 28,
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              bgcolor: '#F5A623',
              borderRadius: 1.5,
            },
            '&::before': {
              width: 8,
              height: 28,
              top: 0,
              left: 10,
            },
            '&::after': {
              width: 28,
              height: 8,
              top: 10,
              left: 0,
            },
          }}
        />
      </Box>

      {/* Tagline */}
      <Typography
        variant="body1"
        sx={{
          color: 'rgba(255,255,255,0.9)',
          mt: 3,
          fontWeight: 600,
          fontSize: { xs: '0.9rem', md: '1.05rem' },
          animation: `${fadeIn} 0.7s ease-out 1.2s both`,
          background: 'linear-gradient(90deg, rgba(255,255,255,0.7), #fff, rgba(255,255,255,0.7))',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animationName: `${fadeIn}, ${shimmer}`,
          animationDuration: '0.7s, 3s',
          animationDelay: '1.2s, 1.9s',
          animationTimingFunction: 'ease-out, linear',
          animationFillMode: 'both, none',
          animationIterationCount: '1, infinite',
        }}
      >
        فرۆشگاک تایبەت بە شوشەوات
      </Typography>

      {/* Loading dots */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mt: 5,
          animation: `${fadeIn} 0.5s ease-out 1.6s both`,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: '#fff',
              animation: `${dotBounce} 1.4s ease-in-out infinite`,
              animationDelay: `${i * 0.16}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
